import { useContext, useEffect } from 'react';
import { UNSAFE_DataRouterStateContext, useLocation } from 'react-router';

/** How long a returning screen has to draw its data before the position is let go. */
export const RESTORE_WAIT_MS = 5000;

/**
 * Scrolls to `y` once the document is tall enough to hold it, and gives up on
 * the first sign the user is scrolling themselves or after `RESTORE_WAIT_MS`.
 * Returns the cancel.
 *
 * Every screen reads its data in an effect on arrival and draws a one-screen
 * skeleton first, so `<ScrollRestoration />`'s own `scrollTo` lands on a
 * document too short to hold the position and clamps. Waiting on the document's
 * height rather than on a loaded flag covers every screen without touching
 * one — To-Do draws no skeleton at all.
 */
export function restoreWhenTall(y: number): () => void {
  const root = document.documentElement;
  const fits = () => root.scrollHeight - window.innerHeight >= y;

  const observer = new MutationObserver(() => {
    if (!fits()) return;
    window.scrollTo(0, y);
    cancel();
  });
  const timer = window.setTimeout(() => cancel(), RESTORE_WAIT_MS);
  const USER_INPUT = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;

  function cancel() {
    observer.disconnect();
    window.clearTimeout(timer);
    for (const type of USER_INPUT) window.removeEventListener(type, cancel);
  }

  if (fits()) {
    window.scrollTo(0, y);
    cancel();
    return cancel;
  }
  observer.observe(document.body, { childList: true, subtree: true, attributes: true });
  for (const type of USER_INPUT) window.addEventListener(type, cancel, { passive: true });
  return cancel;
}

/**
 * Re-applies the position the router restored on a back/forward navigation
 * once the returning screen has drawn its data (HOM-156). Renders beside
 * `<ScrollRestoration />`, and reads the same router state it does.
 */
export function RestoreScrollAfterLoad() {
  const { key } = useLocation();
  const y = useContext(UNSAFE_DataRouterStateContext)?.restoreScrollPosition;

  useEffect(() => {
    if (typeof y !== 'number' || y <= 0) return;
    return restoreWhenTall(y);
  }, [key, y]);

  return null;
}
